import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from '@karma/components';
import { ArrowNoCircleIcon, StarIcon, OldStarIconFilled, TrashIcon } from '@karma/components/icons';
import Typography from '@karma/components/Typography';
import * as ALOOMA from 'consts/alooma';
import Alooma from 'services/alooma';
import { getTagStatus } from 'store/multiEdit/selectors';
import { getProductModalFeature } from 'store/features/selectors';
import { showDeleteTagAlert } from 'store/alerts/actions';
import { toggleTag } from 'store/multiEdit/actions';
import useAction from 'hooks/useAction';
import { deleteTagRequest, updateTagRequest } from 'store/myItems/store/actions';
import TagRegularImageOverlay from '../TagRegularImageOverlay/TagRegularImageOverlay';
import { Overlay, OverlayBottom, OverlayTopGroupRight, StyledCheckbox } from './TagEditableImageOverlay.style';

const TagEditableImageOverlay = ({ tag, displayCheckbox }) => {
  const { t } = useTranslation();
  const [isMouseover, setIsMouseover] = useState(false);

  const { favorites, id, retailer_name: retailerName } = tag;

  const updateTag = useAction(updateTagRequest);
  const toggleTagFunc = useAction(toggleTag);
  const deleteTagRequestFunc = useAction(deleteTagRequest);
  const showDeleteTagAlertFunc = useAction(showDeleteTagAlert);

  const isSelected = useSelector(getTagStatus)(id);
  const productModalFeature = useSelector(getProductModalFeature);

  const contextDataAttrs = Alooma.useContextDataAttrs();

  const showCheckbox = displayCheckbox && (isMouseover || isSelected);

  const checkboxClickHandler = e => {
    e.stopPropagation();
    toggleTagFunc(id);
  };

  const renderButtons = () => {
    if (isSelected) return null;

    return (
      <>
        {(isMouseover || favorites) && (
          <Button
            {...contextDataAttrs}
            type="icon2"
            data-alooma-element={favorites ? ALOOMA.ELEMENTS.REMOVE_FROM_FAVORITES : ALOOMA.ELEMENTS.ADD_TO_FAVORITES}
            Icon={favorites ? OldStarIconFilled : StarIcon}
            size="small"
            active={!!favorites}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              updateTag({ id, data: { favorites: !favorites } });
            }}
          />
        )}
        {isMouseover && (
          <Button
            {...contextDataAttrs}
            data-alooma-element={ALOOMA.ELEMENTS.DELETE}
            type="icon2"
            size="small"
            Icon={TrashIcon}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              deleteTagRequestFunc(id);
              showDeleteTagAlertFunc({
                message: t('app:productDeleted'),
                id,
                source: ALOOMA.SOURCE_PAGES.POP_UP_FOR_DELETE_ITEM,
              });
            }}
          />
        )}
      </>
    );
  };

  return (
    <Overlay
      isMouseover={isMouseover}
      onMouseEnter={() => setIsMouseover(true)}
      onMouseLeave={() => setIsMouseover(false)}
    >
      {showCheckbox && (
        <StyledCheckbox
          {...contextDataAttrs}
          data-alooma-element={ALOOMA.ELEMENTS.CHECK}
          value={isSelected}
          onChange={checkboxClickHandler}
          onClick={e => {
            e.stopPropagation();
          }}
        />
      )}
      <OverlayTopGroupRight justifyContent="flex-start" direction="column" alignItems="flex-start" spacing={8}>
        {renderButtons()}
      </OverlayTopGroupRight>
      {isMouseover ? (
        <OverlayBottom {...contextDataAttrs} data-alooma-element={ALOOMA.ELEMENTS.BUTTON_QUICK_VIEW}>
          <Typography variant="labelExtraSmallUppercase" color="currentColor" rMargin={5}>
            {productModalFeature ? t('app:quickView') : t('app:viewOn', { name: retailerName })}
          </Typography>
          <ArrowNoCircleIcon size="extraSmall" />
        </OverlayBottom>
      ) : (
        <TagRegularImageOverlay tag={tag} />
      )}
    </Overlay>
  );
};

export default TagEditableImageOverlay;
